// Etapas 2 e 3 do onboarding compartilham o mesmo layout de questionario Likert.
import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERSONALITY_GROUPS, HABITS_STATEMENTS, LIKERT_LEGEND } from "@/data/questionnaire";

function LikertRow({ code, text, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5" data-testid={`likert-${code}`}>
      <span className="text-sm text-[#3a3550] flex-1">{text}</span>
      <div className="flex gap-1.5 shrink-0">
        {[1, 2, 3, 4, 5].map((v) => (
          <button key={v} type="button" data-testid={`likert-${code}-${v}`} onClick={() => onChange(code, v)}
            className={`w-8 h-8 rounded-lg text-sm font-semibold border transition-colors ${value === v ? "mc-grad text-white border-transparent" : "bg-white text-[#6b6580] border-[#e3dff0] hover:border-violet-400"}`}>
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

function QuestionnaireShell({ badge, title, children, onSubmit, cta, loading }) {
  return (
    <div className="mc-container py-14 flex justify-center" data-testid="page-onboarding-q">
      <div className="mc-card w-full max-w-2xl p-8 mc-fade-up">
        <span className="inline-block mc-grad text-white text-xs font-bold px-4 py-1.5 rounded-full">{badge}</span>
        <h1 className="text-3xl font-bold text-[#1e1b2e] mt-4">{title}</h1>
        <p className="text-sm text-muted-foreground mt-3">Responda às afirmações abaixo com base em como você geralmente se sente ou age. Use a seguinte escala de 1 a 5:</p>
        <p className="text-sm text-[#1e1b2e] font-medium mt-1">{LIKERT_LEGEND}</p>
        <form onSubmit={onSubmit} className="mt-6">
          {children}
          <button className="btn-primary w-full mt-8" data-testid="q-submit" disabled={loading}>{loading ? "Salvando..." : cta}</button>
        </form>
      </div>
    </div>
  );
}

export function Personalidade() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const onChange = (code, v) => setAnswers((a) => ({ ...a, [code]: v }));

  if (user === false) return <Navigate to="/cadastro" replace />;
  // Guarda de sequencia (blueprint 24.9): precisa ter concluido etapa 1.
  if (user && user !== null && (user.onboarding_step || 1) < 2) return <Navigate to="/cadastro" replace />;

  const total = PERSONALITY_GROUPS.reduce((s, g) => s + g.statements.length, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (Object.keys(answers).length < total) { toast.error("Responda todas as afirmações"); return; }
    setLoading(true);
    try {
      const payload = Object.entries(answers).map(([statement_code, value]) => ({ statement_code, value }));
      await api.post("/onboarding/step-2", { answers: payload });
      await refreshMe();
      navigate("/cadastro/habitos");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <QuestionnaireShell badge="ETAPA 1 - 5 PERGUNTAS" title="Sobre sua Personalidade" onSubmit={submit} cta="Próximo" loading={loading}>
      {PERSONALITY_GROUPS.map((g) => (
        <div key={g.trait} className="mt-6">
          <h3 className="text-lg font-semibold text-[#1e1b2e] mb-2">{g.title}</h3>
          {g.statements.map((s, i) => {
            const code = `${g.trait}_${i + 1}`;
            return <LikertRow key={code} code={code} text={s} value={answers[code]} onChange={onChange} />;
          })}
        </div>
      ))}
    </QuestionnaireShell>
  );
}

export function Habitos() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const onChange = (code, v) => setAnswers((a) => ({ ...a, [code]: v }));

  if (user === false) return <Navigate to="/cadastro" replace />;
  if (user && user !== null && (user.onboarding_step || 1) < 3) return <Navigate to="/cadastro/personalidade" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (Object.keys(answers).length < HABITS_STATEMENTS.length) { toast.error("Responda todas as afirmações"); return; }
    setLoading(true);
    try {
      const payload = Object.entries(answers).map(([statement_code, value]) => ({ statement_code, value }));
      await api.post("/onboarding/step-3", { answers: payload });
      await refreshMe();
      toast.success("Cadastro concluído! Bem-vindo ao Mora Comigo.");
      navigate("/feed");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <QuestionnaireShell badge="ETAPA 2 - 1 PERGUNTA" title="Sobre Hábitos e Preferências" onSubmit={submit} cta="Concluir cadastro" loading={loading}>
      <div className="mt-6">
        {HABITS_STATEMENTS.map((s, i) => {
          const code = `habit_${i + 1}`;
          return <LikertRow key={code} code={code} text={s} value={answers[code]} onChange={onChange} />;
        })}
      </div>
    </QuestionnaireShell>
  );
}
